import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { SOC_CODES, socLabel } from "../lib/soc_codes";

/**
 * Searchable SOC 2020 code + occupation title picker.
 * Calls onPick({ code, title }) when the user selects an entry.
 */
export default function SocPicker({ value, onPick, placeholder = "Search SOC code or title…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SOC_CODES.slice(0, 100);
    return SOC_CODES.filter(
      (e) => e.code.includes(q) || e.title.toLowerCase().includes(q),
    ).slice(0, 100);
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="soc-picker-trigger"
          className="w-full justify-between bg-[#0b0d12] border-white/10 text-slate-100 font-normal hover:bg-white/5"
        >
          <span className="truncate">
            {value ? value : <span className="text-slate-500">Pick a SOC code / occupation…</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(560px,calc(100vw-2rem))] p-0 bg-[#12151d] border-white/10"
        align="start"
      >
        <div className="p-2 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
            <Input
              autoFocus
              data-testid="soc-picker-search"
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-[#0b0d12] border-white/10 text-slate-100 pl-8 h-9"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-500">
              No matching SOC codes
            </div>
          ) : (
            results.map((e) => {
              const label = socLabel(e);
              const isSelected = value === label;
              return (
                <button
                  key={e.code}
                  type="button"
                  data-testid={`soc-option-${e.code}`}
                  onClick={() => {
                    onPick?.({ code: e.code, title: e.title, label });
                    setOpen(false);
                    setQuery("");
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/5 flex items-start gap-3"
                >
                  <span className="w-12 flex-shrink-0 font-mono text-[#f2c14e]">
                    {e.code}
                  </span>
                  <span className="flex-1">{e.title}</span>
                  {isSelected && <Check className="w-4 h-4 text-emerald-400" />}
                </button>
              );
            })
          )}
        </div>
        <div className="px-3 py-2 border-t border-white/5 text-xs text-slate-500">
          {results.length === 100 ? "Showing first 100 — refine your search" : `${results.length} result${results.length === 1 ? "" : "s"}`}
        </div>
      </PopoverContent>
    </Popover>
  );
}
